import { isEmpty } from 'lodash-es';
import React, { FC, useState, useEffect } from 'react';
import { ICandidate, ICandidateExperience, ITimePeriod } from 'wisestep-api';

import { Label } from '../Label';
import WsTypography, { WsTypoVariants, WsTypoColors } from '../WsTypography/WsTypography';

interface ICandidateWorkHistory {
  candidate: ICandidate;
  mt?: number;
}

const CandidateWorkHistory: FC<ICandidateWorkHistory> = ({ candidate, mt }) => {
  const uniqueHistory = Array.from(
    new Set(candidate?.experience ? candidate?.experience.history : [])
  );
  const limit = 2;
  const hiddenHistory: string = '+' + String(uniqueHistory.length - limit) + ' More';
  const isHistoryMoreThenLimit = uniqueHistory.length > limit;
  const tempUniqueHistory = [...uniqueHistory];
  const minUniqueHistory = tempUniqueHistory.slice(0, limit);
  const [requestForMoreHistory, setRequestForMoreHistory] = useState<boolean>(false);
  const [historyWithLimit, setHistoryWithLimit] = useState<ICandidateExperience[]>([]);

  useEffect(() => {
    if (isEmpty(historyWithLimit) && !isEmpty(minUniqueHistory)) {
      setHistoryWithLimit(minUniqueHistory);
    }
  }, [historyWithLimit, minUniqueHistory]);

  const increaseTheLimit = () => {
    setRequestForMoreHistory(true);
    setHistoryWithLimit(uniqueHistory);
  };

  const decreaseTheLimit = () => {
    setRequestForMoreHistory(false);
    setHistoryWithLimit(minUniqueHistory);
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const getTitles = (title: string, company: string) => {
    if (!isEmpty(title) && !isEmpty(company)) {
      return `${title} @${company}`;
    } else if (!isEmpty(title)) {
      return title;
    } else if (!isEmpty(company)) {
      return `@${company}`;
    } else {
      return '';
    }
  };

  const getDate = (history: ICandidateExperience) => {
    const field: ITimePeriod | null = history.periodStart;
    if (field && field?.month > 0 && field?.year > 0) {
      const field1: ITimePeriod | null = history.periodEnd;
      if (field1 && field1?.month > 0 && field1?.year > 0) {
        return `From ${monthNames[field.month - 1]} ${field.year} to ${
          monthNames[field1.month - 1]
        } ${field1.year}`;
      } else {
        return '';
      }
    } else {
      return '';
    }
  };

  return (
    <>
      {mt != undefined && <Label sx={{ mt: `${mt}px` }}></Label>}
      {historyWithLimit.map((history: ICandidateExperience, index: number) => (
        <React.Fragment key={index}>
          <WsTypography>{getTitles(history.title ?? '', history.org ?? '')}</WsTypography>
          {getDate(history) && (
            <WsTypography color={WsTypoColors.SECONDARY} bold={true}>
              {getDate(history)}
            </WsTypography>
          )}
        </React.Fragment>
      ))}
      {isEmpty(historyWithLimit) &&
        candidate.previousJobTitles &&
        candidate.previousJobTitles.map((title, index) => (
          <WsTypography key={index} bold={true} display="inline">
            {`${index > 0 ? `, ${title}` : title}`}
          </WsTypography>
        ))}
      {!requestForMoreHistory && isHistoryMoreThenLimit && (
        <WsTypography
          variant={WsTypoVariants.BODY2}
          color={WsTypoColors.PRIMARY}
          onClick={increaseTheLimit}
          cursor="pointer"
        >
          {hiddenHistory}
        </WsTypography>
      )}
      {requestForMoreHistory && (
        <WsTypography
          variant={WsTypoVariants.BODY2}
          color={WsTypoColors.PRIMARY}
          cursor="pointer"
          onClick={decreaseTheLimit}
        >
          Hide
        </WsTypography>
      )}
    </>
  );
};

export default CandidateWorkHistory;
